/**
 * Route Corridors Repository - Data access layer for route corridors
 */

import api from "../../../shared/api.jsx";

const ROUTE_CORRIDORS_ENDPOINTS = {
  base: "/route-corridors",
  active: "/route-corridors/active",
  byId: (id) => `/route-corridors/${id}`,
};

/**
 * Get all route corridors with optional filters
 * @param {Object} filters - Filter options
 * @param {boolean} [filters.active] - Active status filter
 * @param {string} [filters.search] - Search by name or description
 * @returns {Promise<Object>} API response
 */
export const fetchRouteCorridors = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.active !== undefined) params.append("active", filters.active.toString());
  if (filters.search) params.append("search", filters.search);

  const url = params.toString()
    ? `${ROUTE_CORRIDORS_ENDPOINTS.base}?${params}`
    : ROUTE_CORRIDORS_ENDPOINTS.base;
  const response = await api.get(url);
  return {
    success: true,
    data: response.data || response,
    count: response.count || (response.data ? response.data.length : 0),
  };
};

/**
 * Get active route corridors (for dropdowns)
 * @returns {Promise<Object>} API response
 */
export const fetchActiveRouteCorridors = async () => {
  const response = await api.get(ROUTE_CORRIDORS_ENDPOINTS.active);
  return {
    success: true,
    data: response.data || response,
  };
};

/**
 * Get a single route corridor by ID
 * @param {string} routeCorridorId - Route corridor identifier
 * @returns {Promise<Object>} API response
 */
export const fetchRouteCorridorById = async (routeCorridorId) => {
  const response = await api.get(ROUTE_CORRIDORS_ENDPOINTS.byId(routeCorridorId));
  // API client returns JSON directly, check if it has success property
  if (response && response.success !== undefined) {
    return {
      success: response.success,
      data: response.data || response,
    };
  }
  // Fallback if response is already the data
  return {
    success: true,
    data: response.data || response,
  };
};

/**
 * Create a new route corridor
 * @param {Object} routeCorridorData - Route corridor data
 * @returns {Promise<Object>} API response
 */
export const createRouteCorridor = async (routeCorridorData) => {
  const response = await api.post(ROUTE_CORRIDORS_ENDPOINTS.base, routeCorridorData);
  return response;
};

/**
 * Update an existing route corridor
 * @param {string} routeCorridorId - Route corridor identifier
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object>} API response
 */
export const updateRouteCorridor = async (routeCorridorId, updateData) => {
  const response = await api.put(ROUTE_CORRIDORS_ENDPOINTS.byId(routeCorridorId), updateData);
  return response;
};

/**
 * Delete a route corridor
 * @param {string} routeCorridorId - Route corridor identifier
 * @returns {Promise<Object>} API response
 */
export const deleteRouteCorridor = async (routeCorridorId) => {
  const response = await api.delete(ROUTE_CORRIDORS_ENDPOINTS.byId(routeCorridorId));
  return response;
};

